import Component from '@glimmer/component';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import {
  PhShieldCheck,
  PhFileText,
  PhArrowsClockwise,
  PhNewspaper,
  PhCheckCircle,
  PhWarning,
  PhSpinner,
  PhLightning,
} from 'ember-phosphor-icons';
import { eq } from 'tio-ui/utilities';
import type {
  PslfAutomationStatus,
  PslfProtectionStatus,
} from 'tio-employee/services/career-pathways-api';

interface ControlTowerSignature {
  Args: {
    automationStatus: PslfAutomationStatus;
    protectionStatus: PslfProtectionStatus;
  };
  Element: HTMLDivElement;
}

// Helper to get status icon + semantic intent. `color` / `bgColor` route
// through the `.tio-{fg,bg}-on-dark-*` utilities (always-dark glass surface,
// mode-independent by design). Note `easy_mode` keeps the yellow/warning hue;
// `syncing`/`monitoring` map to `info` (teal) — these ARE genuine in-progress
// status states, so info is the correct intent (not brand).
function getStatusDisplay(status: string): {
  icon: typeof PhCheckCircle;
  color: string;
  bgColor: string;
} {
  switch (status) {
    case 'automated':
    case 'synced':
    case 'protected':
      return {
        icon: PhCheckCircle,
        color: 'tio-fg-on-dark-success',
        bgColor: 'tio-bg-on-dark-tint-success',
      };
    case 'easy_mode':
      return {
        icon: PhLightning,
        color: 'tio-fg-on-dark-warning',
        bgColor: 'tio-bg-on-dark-tint-warning',
      };
    case 'syncing':
    case 'monitoring':
      return {
        icon: PhSpinner,
        color: 'tio-fg-on-dark-info',
        bgColor: 'tio-bg-on-dark-tint-info',
      };
    case 'action_needed':
    case 'alert':
    case 'expired':
    case 'stale':
    case 'error':
      return {
        icon: PhWarning,
        color: 'tio-fg-on-dark-danger',
        bgColor: 'tio-bg-on-dark-tint-danger',
      };
    default:
      return {
        icon: PhCheckCircle,
        color: 'tio-fg-on-dark-neutral',
        bgColor: 'tio-bg-on-dark-tint-neutral',
      };
  }
}

export default class PslfControlTower extends Component<ControlTowerSignature> {
  get ecfDisplay() {
    return getStatusDisplay(this.args.automationStatus.ecfStatus);
  }

  get idrDisplay() {
    return getStatusDisplay(this.args.automationStatus.idrStatus);
  }

  get syncDisplay() {
    return getStatusDisplay(this.args.automationStatus.syncStatus);
  }

  get policyDisplay() {
    return getStatusDisplay(this.args.automationStatus.policyStatus);
  }

  get threatLevelColor() {
    switch (this.args.protectionStatus.threatLevel) {
      case 'low':
        return 'emerald';
      case 'medium':
        return 'amber';
      case 'high':
        return 'red';
      default:
        return 'gray';
    }
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <div
      class="tio-gradient-dark-glass rounded-2xl p-6 tio-fg-inverse shadow-xl"
      data-test-control-tower
      ...attributes
    >
      {{! Header }}
      <div class="tio-row mb-6">
        <div class="p-2 tio-bg-on-dark-tint-brand rounded-xl">
          <span class="text-2xl">🎛️</span>
        </div>
        <div>
          <Heading @level={{2}} @size="xl" class="tio-fg-inverse font-bold">
            PSLF Control Tower
          </Heading>
          <Text @size="sm" class="tio-fg-disabled">Your autopilot status</Text>
        </div>
      </div>

      {{! Status Modules }}
      <div class="space-y-3">
        {{! Employer Certification }}
        <div class="bg-white/5 rounded-xl p-4 border border-white/10" data-test-ecf-section>
          <div class="tio-row-between">
            <div class="tio-row">
              <div class="p-2 {{this.ecfDisplay.bgColor}} rounded-lg">
                <PhShieldCheck @size={{20}} class="{{this.ecfDisplay.color}}" />
              </div>
              <div>
                <Text @weight="medium" class="tio-fg-inverse">Employer Certification</Text>
                <Text @size="sm" class="tio-fg-disabled">{{@automationStatus.ecfDetails}}</Text>
              </div>
            </div>
            <div
              class="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide
                {{if
                  (eq @automationStatus.ecfStatus 'automated')
                  'tio-bg-on-dark-tint-success tio-fg-on-dark-success'
                }}
                {{if
                  (eq @automationStatus.ecfStatus 'manual')
                  'tio-bg-on-dark-tint-neutral tio-fg-on-dark-neutral'
                }}
                {{if
                  (eq @automationStatus.ecfStatus 'action_needed')
                  'tio-bg-on-dark-tint-danger tio-fg-on-dark-danger'
                }}"
              data-test-ecf-status
            >
              {{@automationStatus.ecfStatusLabel}}
            </div>
          </div>
          {{#if @automationStatus.ecfLastSubmitted}}
            <div class="mt-2 text-xs tio-fg-muted" data-test-ecf-last-submitted>
              Last submitted:
              {{@automationStatus.ecfLastSubmitted}}
            </div>
          {{/if}}
        </div>

        {{! IDR Recertification }}
        <div class="bg-white/5 rounded-xl p-4 border border-white/10" data-test-idr-section>
          <div class="tio-row-between">
            <div class="tio-row">
              <div class="p-2 {{this.idrDisplay.bgColor}} rounded-lg">
                <PhFileText @size={{20}} class="{{this.idrDisplay.color}}" />
              </div>
              <div>
                <Text @weight="medium" class="tio-fg-inverse">IDR Recertification</Text>
                <Text @size="sm" class="tio-fg-disabled">{{@automationStatus.idrDetails}}</Text>
              </div>
            </div>
            <div
              class="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide
                {{if
                  (eq @automationStatus.idrStatus 'automated')
                  'tio-bg-on-dark-tint-success tio-fg-on-dark-success'
                }}
                {{if
                  (eq @automationStatus.idrStatus 'easy_mode')
                  'tio-bg-on-dark-tint-warning tio-fg-on-dark-warning'
                }}
                {{if
                  (eq @automationStatus.idrStatus 'action_needed')
                  'tio-bg-on-dark-tint-danger tio-fg-on-dark-danger'
                }}
                {{if
                  (eq @automationStatus.idrStatus 'expired')
                  'tio-bg-on-dark-tint-danger tio-fg-on-dark-danger'
                }}"
              data-test-idr-status
            >
              {{@automationStatus.idrStatusLabel}}
            </div>
          </div>
          {{#if @automationStatus.idrPlanExpires}}
            <div class="mt-2 text-xs tio-fg-muted" data-test-idr-plan-expires>
              Current plan expires:
              {{@automationStatus.idrPlanExpires}}
            </div>
          {{/if}}
        </div>

        {{! Payment Verification }}
        <div class="bg-white/5 rounded-xl p-4 border border-white/10" data-test-sync-section>
          <div class="tio-row-between">
            <div class="tio-row">
              <div class="p-2 {{this.syncDisplay.bgColor}} rounded-lg">
                <PhArrowsClockwise
                  @size={{20}}
                  class="{{this.syncDisplay.color}}
                    {{if (eq @automationStatus.syncStatus 'syncing') 'animate-spin'}}"
                />
              </div>
              <div>
                <Text @weight="medium" class="tio-fg-inverse">Payment Verification</Text>
                <Text @size="sm" class="tio-fg-disabled">{{@automationStatus.syncDetails}}</Text>
              </div>
            </div>
            <div
              class="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide
                {{if
                  (eq @automationStatus.syncStatus 'synced')
                  'tio-bg-on-dark-tint-success tio-fg-on-dark-success'
                }}
                {{if
                  (eq @automationStatus.syncStatus 'syncing')
                  'tio-bg-on-dark-tint-info tio-fg-on-dark-info'
                }}
                {{if
                  (eq @automationStatus.syncStatus 'stale')
                  'tio-bg-on-dark-tint-warning tio-fg-on-dark-warning'
                }}
                {{if
                  (eq @automationStatus.syncStatus 'error')
                  'tio-bg-on-dark-tint-danger tio-fg-on-dark-danger'
                }}"
              data-test-sync-status
            >
              {{@automationStatus.syncStatusLabel}}
            </div>
          </div>
          <div class="mt-2 text-xs tio-fg-muted" data-test-sync-last-updated>
            Last sync:
            {{@automationStatus.syncLastUpdated}}
          </div>
        </div>

        {{! Policy Monitor }}
        <div class="bg-white/5 rounded-xl p-4 border border-white/10" data-test-policy-section>
          <div class="tio-row-between">
            <div class="tio-row">
              <div class="p-2 {{this.policyDisplay.bgColor}} rounded-lg">
                <PhNewspaper @size={{20}} class="{{this.policyDisplay.color}}" />
              </div>
              <div>
                <Text @weight="medium" class="tio-fg-inverse">Policy Monitor</Text>
                <Text @size="sm" class="tio-fg-disabled">{{@automationStatus.policyDetails}}</Text>
              </div>
            </div>
            <div
              class="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide
                {{if
                  (eq @automationStatus.policyStatus 'protected')
                  'tio-bg-on-dark-tint-success tio-fg-on-dark-success'
                }}
                {{if
                  (eq @automationStatus.policyStatus 'monitoring')
                  'tio-bg-on-dark-tint-info tio-fg-on-dark-info'
                }}
                {{if
                  (eq @automationStatus.policyStatus 'alert')
                  'tio-bg-on-dark-tint-danger tio-fg-on-dark-danger'
                }}"
              data-test-policy-status
            >
              {{@automationStatus.policyStatusLabel}}
            </div>
          </div>
        </div>
      </div>

      {{! Threat Level Indicator }}
      <div class="mt-6 pt-4 border-t border-white/10">
        <div class="tio-row-between">
          <Text @size="sm" class="tio-fg-disabled">Overall Protection Level</Text>
          <div class="tio-row-tight">
            <div
              class="w-3 h-3 rounded-full
                {{if (eq this.threatLevelColor 'emerald') 'tio-bg-on-dark-success'}}
                {{if (eq this.threatLevelColor 'amber') 'tio-bg-on-dark-warning'}}
                {{if (eq this.threatLevelColor 'red') 'tio-bg-on-dark-danger'}}"
            ></div>
            {{! Plain <span> (not <Text>): <Text> injects `dark:text-gray-100`
                which out-cascades the intent color in dark mode. }}
            <span
              class="font-bold uppercase text-sm
                {{if (eq this.threatLevelColor 'emerald') 'tio-fg-on-dark-success'}}
                {{if (eq this.threatLevelColor 'amber') 'tio-fg-on-dark-warning'}}
                {{if (eq this.threatLevelColor 'red') 'tio-fg-on-dark-danger'}}"
              data-test-threat-level
            >
              {{@protectionStatus.threatLevel}}
              RISK
            </span>
          </div>
        </div>
        {{#if @protectionStatus.grandfathered}}
          {{! Plain <span> (not <Text>): keeps the success-green intent in dark mode. }}
          <span class="tio-fg-on-dark-success text-xs mt-2 block" data-test-grandfathered>
            🛡️ Grandfathered since
            {{@protectionStatus.grandfatheredSince}}
            — your eligibility is protected
          </span>
        {{/if}}
      </div>
    </div>
  </template>
}
